import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { LogOut, LayoutDashboard, User, ChevronDown, Globe, Bell, Inbox } from "lucide-react";
import NotificationBell from "./NotificationBell";

const LOGO_URL = "/motravay-logo.png";

export default function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();

  const dashHref = user?.role === "admin" ? "/admin" : user?.role === "employer" ? "/employer" : "/candidate";

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group" aria-label="Motravay home">
          <img
            src={LOGO_URL}
            alt="Motravay logo"
            className="h-10 w-auto object-contain"
            width="160" height="40" loading="eager"
            style={{ filter: "drop-shadow(0 0 0 transparent)" }}
            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'inline'; }}
          />
          <span style={{ display: 'none' }} className="font-display text-2xl font-bold text-[#1A8FE3]">MOTRAVAY</span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
          <Link to="/jobs" data-testid="nav-jobs" className="hover:text-[#1A8FE3] transition-colors">{t("nav.jobs")}</Link>
          {!user && (
            <Link to="/register?role=employer" data-testid="nav-employers" className="hover:text-[#1A8FE3] transition-colors">{t("nav.employers")}</Link>
          )}
          {user?.role === "employer" && (
            <Link to="/employer/post" data-testid="nav-post-job" className="hover:text-[#1A8FE3] transition-colors">{t("nav.postJob")}</Link>
          )}
        </nav>

        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="lang-toggle" className="text-slate-700 hover:text-[#1A8FE3] gap-1">
                <Globe className="h-4 w-4" />
                {lang.toUpperCase()}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLang("en")} data-testid="lang-en">English</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLang("fr")} data-testid="lang-fr">Français</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user && (
            <>
              <NotificationBell />
              <Link to="/inbox" data-testid="header-inbox" aria-label="Inbox" className="p-2 rounded-full hover:bg-slate-100 text-slate-700">
                <Inbox className="h-5 w-5" />
              </Link>
            </>
          )}

          {!user ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="header-login" className="text-slate-700 hover:text-[#1A8FE3]">
                {t("nav.login")}
              </Button>
              <Button size="sm" onClick={() => navigate("/register")} data-testid="header-signup" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
                {t("nav.signup")}
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu" className="text-slate-700 hover:text-[#1A8FE3] gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">{user.name || user.company_name || user.email}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => navigate(dashHref)} data-testid="menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
